import type { Group } from "building-types/groups/group";
import type { OpeningHandle } from "building-types/handles/docEntityHandleTypes";
import type {
  Opening,
  OpeningNoExpr,
  OpeningNoExprDescriptor,
  SolidOpeningPlacement,
} from "building-types/openings/opening";
import type {
  ManualOpeningPlacement,
  OpeningPlacementDescriptorTable,
} from "building-types/openings/openingPlacementTypes";
import { manualOpeningPlacementNew } from "building-types/openings/openingPlacementTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { RoomLayout, RoomLayoutInstance, EntityInstance } from "building-types/roomLayouts/roomLayoutTypes";
import type { RelatedRoomInfo } from "building-types/rooms/relatedRoomsMapTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { evalFormulas } from "doc/formula/evalFormulas";
import {
  buildRoomLayoutInstancesByRoomLayoutId,
  entityHandlesIfRoomLayoutInstanceOverride,
  entityInstancesForEntity,
} from "doc/rooms/layout/roomLayoutTools";
import { copyEntityForComponentsWithOptions } from "../../utils/componentInstanceUtils";

interface MakeRoomOpeningPlacementTablesOptions {
  readonly rooms: Table<Room>;
  /** Doc static rooms for room layout edge remapping (includes component template rooms). */
  readonly docRooms: Table<StaticRoom>;
  readonly openings: Table<OpeningNoExpr>;
  readonly openingGroups: Table<string>;
  readonly groups: Table<Group>;
  readonly roomLayouts: Table<RoomLayout>;
  readonly roomLayoutInstances: Table<RoomLayoutInstance>;
  readonly entityRoomLayouts: Table<RoomLayout["id"]>;
  readonly optionChoices: OptionChoices; // Filtered to options referenced by openings
  readonly rawOptionChoices: OptionChoices;
  readonly entityDisabledByGroup: Table<boolean>;
  readonly definitions: OptionDefinitions;
  readonly relatedRoomsMap: {
    readonly [roomId: string]: ReadonlyArray<RelatedRoomInfo>;
  };
}

export function makeRoomOpeningPlacementTables(
  {
    rooms,
    docRooms,
    openings,
    openingGroups,
    groups,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    optionChoices,
    rawOptionChoices,
    entityDisabledByGroup,
    relatedRoomsMap,
    definitions,
  }: MakeRoomOpeningPlacementTablesOptions,
  forEveryOpeningPlacement: ((handle: OpeningHandle, pos: number[], room: Room) => void) | null = null,
) {
  const manualOpeningPlacementTables: ManualOpeningPlacementTables = {};
  const solidWallOverrides: RWTable<SolidOpeningPlacement> = {};
  const hasRoomLayouts = !!Object.keys(entityRoomLayouts).length;
  const roomLayoutInstancesByRoomLayoutId = hasRoomLayouts
    ? buildRoomLayoutInstancesByRoomLayoutId(roomLayoutInstances)
    : undefined;

  const openingsWithInstances: RWTable<OpeningNoExpr> = {};

  for (const openingId in openings) {
    const opening = openings[openingId];
    const archetypeOpening = evalFormulas({ ...opening }, (v) => optionChoices[v], undefined, entityDisabledByGroup);

    const archetypeInstances = entityInstancesForEntity({
      hasRoomLayouts,
      docEntityRoomLayouts: entityRoomLayouts,
      docRoomLayouts: roomLayouts,
      rooms: docRooms,
      activeRoomLayoutInstances: roomLayoutInstances,
      roomLayoutInstancesByRoomLayoutId,
      entity: archetypeOpening,
    });

    // Combine room layout instances and component instances into one unified array.
    const allInstances: EntityInstance[] = [];
    // Map to store evaluated openings for component instances, keyed by instanceId.
    const componentOpenings: Map<string, OpeningNoExpr> = new Map();
    // Populate allInstances and componentOpenings with archetype (archetypeInstances) and component instance placements
    copyEntityForComponentsWithOptions(
      opening,
      relatedRoomsMap,
      archetypeInstances,
      definitions,
      rawOptionChoices,
      allInstances,
      componentOpenings,
      roomLayoutInstances,
    );

    for (const instance of allInstances) {
      const { entityInstanceType, parentId, edgeId, handleOverride, instanceId } = instance;
      const entityRoom = parentId !== undefined ? rooms[parentId] : undefined;
      if (!entityRoom) {
        continue;
      }

      const { handle, handleAuxiliary } = entityHandlesIfRoomLayoutInstanceOverride(
        { type: "OPENING", id: opening.id },
        handleOverride,
      );

      assertIsNotNullOrUndefined(parentId);

      if (typeof edgeId === "string") {
        const id =
          entityInstanceType === EntityInstanceType.ROOM_LAYOUT_INSTANCE || instance.instanceId ? uid() : opening.id;
        // Use instance-evaluated opening for component instances, otherwise use archetype-evaluated opening.
        const componentOpening = instanceId ? componentOpenings.get(instanceId) : undefined;
        const openingToUse = componentOpening ?? archetypeOpening;
        if (openingToUse.disabled && !forEveryOpeningPlacement) {
          continue;
        }
        const openingInstance: OpeningNoExpr = {
          ...openingToUse,
          parentId,
          edgeId,
          id,
          handle,
          handleAuxiliary,
          entityInstanceType,
        };

        openingsWithInstances[openingInstance.id] = openingInstance;
      }
    }
  }

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const roomOpenings =
      Object.values(openingsWithInstances)
        .filter((x) => x.parentId === roomId)
        .sort((a, b) => a.sortIndex - b.sortIndex) ?? [];

    if (!roomOpenings.length) {
      continue;
    }

    const { parentId: blockId } = room;
    const { manualOpeningsByEdgeTable, edgeIsFromDeprecatedOpeningPattern, openingEdgeIsExclusive } =
      makeManualOpeningPlacementTable(roomOpenings, forEveryOpeningPlacement, room, openingGroups, groups);

    makeManualOpeningPlacementsFromEdgeTables(
      manualOpeningsByEdgeTable,
      blockId,
      roomId,
      edgeIsFromDeprecatedOpeningPattern,
      openingEdgeIsExclusive,
      solidWallOverrides,
      manualOpeningPlacementTables,
      room,
    );
  }

  return {
    manualOpeningPlacementTables,
    solidWallOverrides,
  };
}

/**
 * If exclusive group found, only enable the first exclusive group found per room edge.
 */
function makeManualOpeningPlacementTable(
  roomOpenings: OpeningNoExpr[],
  forEveryOpeningPlacement: ((handle: OpeningHandle, pos: number[], room: Room) => void) | null,
  room: Room,
  openingGroups: Table<string>,
  groups: Table<Group>,
) {
  const manualOpeningsByEdgeTable: { [edgeId: string]: Opening[] } = {};
  const openingEdgeIsExclusive: { [edgeId: string]: boolean } = {};
  const firstExclusiveGroupIdByEdgeTable: { [edgeId: string]: string } = {};
  const edgeIsFromDeprecatedOpeningPattern: { [edgeId: string]: boolean } = {};

  for (const openingIndex of Object.keys(roomOpenings)) {
    const opening = roomOpenings[openingIndex];

    if (forEveryOpeningPlacement) {
      const i = room.edgeIds.indexOf(opening.edgeId) * 2;
      const x0 = room.outline[i];
      const y0 = room.outline[i + 1];
      const x1 = room.outline[i + 2];
      const y1 = room.outline[i + 3];
      const edgeMidpoint = [(x0 + x1) * 0.5, (y0 + y1) * 0.5];
      // opening.id is a fresh id per component instance (see the `id` assignment above); opening.handle
      // carries the stable archetype id the option-set hierarchies actually reference.
      forEveryOpeningPlacement({ type: "OPENING", id: opening.handle?.id ?? opening.id }, edgeMidpoint, room);
    }

    if (opening.disabled) {
      continue;
    }

    const id = opening.id;
    const groupId = openingGroups[id];

    if (groupId) {
      const group = groups[groupId];
      if (group.exclusive) {
        if (firstExclusiveGroupIdByEdgeTable[opening.edgeId] === undefined) {
          firstExclusiveGroupIdByEdgeTable[opening.edgeId] = groupId;
        }
        edgeIsFromDeprecatedOpeningPattern[opening.edgeId] = true;
        if (group.id !== firstExclusiveGroupIdByEdgeTable[opening.edgeId]) {
          continue;
        }

        if (opening.type === "DOOR" || opening.type === "WINDOW" || opening.type === "SOLID") {
          openingEdgeIsExclusive[opening.edgeId] = opening.wallStripDomain;
        } else if (opening.type === "DOOR_V2" || opening.type === "WINDOW_V2") {
          openingEdgeIsExclusive[opening.edgeId] = true;
        }

        const arr = manualOpeningsByEdgeTable[opening.edgeId] || [];
        arr.push(opening);
        manualOpeningsByEdgeTable[opening.edgeId] = arr;
      } else {
        const arr = manualOpeningsByEdgeTable[opening.edgeId] || [];
        arr.push(opening);
        manualOpeningsByEdgeTable[opening.edgeId] = arr;
      }
    } else {
      const arr = manualOpeningsByEdgeTable[opening.edgeId] || [];
      arr.push(opening);
      manualOpeningsByEdgeTable[opening.edgeId] = arr;
    }
  }

  return { manualOpeningsByEdgeTable, edgeIsFromDeprecatedOpeningPattern, openingEdgeIsExclusive };
}

export type ManualOpeningPlacementTables = { [roomId: string]: ManualOpeningPlacement[] };

function makeManualOpeningPlacementsFromEdgeTables(
  roomOpeningByEdgeTable: { [edgeId: string]: Opening[] },
  blockId: string,
  roomId: string,
  edgeIsFromDeprecatedOpeningPattern: { [edgeId: string]: boolean },
  openingEdgeIsExclusive: { [edgeId: string]: boolean },
  solidWallOverrides: RWTable<SolidOpeningPlacement>,
  manualOpeningPlacementTables: ManualOpeningPlacementTables,
  room: Room,
) {
  const manualOpeningPlacements: ManualOpeningPlacement[] = [];

  for (const edgeId in roomOpeningByEdgeTable) {
    const openings = roomOpeningByEdgeTable[edgeId];
    if (openings.length === 1 && openings[0].type === "SOLID") {
      manualOpeningPlacements.push(
        manualOpeningPlacementNew(
          blockId,
          roomId,
          openings[0].edgeId,
          openingPlacementTableFromArray([]),
          edgeIsFromDeprecatedOpeningPattern[openings[0].edgeId] ? openingEdgeIsExclusive[openings[0].edgeId] : true,
        ),
      );

      solidWallOverrides[openings[0].id] = { ...openings[0] };
      continue;
    }

    manualOpeningPlacements.push(
      manualOpeningPlacementNew(
        blockId,
        roomId,
        edgeId,
        openingPlacementTableFromArray(openings.filter((x) => x.type !== "SOLID") as OpeningNoExprDescriptor[]),
        edgeIsFromDeprecatedOpeningPattern[openings[0].edgeId] ? openingEdgeIsExclusive[openings[0].edgeId] : true,
      ),
    );
  }

  if (manualOpeningPlacements.length > 0) {
    manualOpeningPlacementTables[room.id] = manualOpeningPlacements;
  }
}

function openingPlacementTableFromArray(openings: OpeningNoExprDescriptor[]): OpeningPlacementDescriptorTable {
  const openingTable: OpeningPlacementDescriptorTable = {};
  for (let i = 0, l = openings.length; i < l; i++) {
    const opening = openings[i];

    if (opening.disabled) {
      continue;
    }

    openingTable[i] = opening;
  }

  return openingTable;
}
